import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { FormFooter, PageHeader, Row, Section, Select, Tabs, Toggle } from '../components/form';
import { Alert, Button, Input } from '../components/ui';
import { api } from '../lib/api';

interface General {
  alertEmail: string;
  systemName: string;
  maxCallDurationSec: number;
  timezone: string;
  toneRegion: string;
  defaultLanguage: string;
}

interface Email {
  enabled: boolean;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  fromAddress: string;
  fromName: string;
}

interface Recording {
  format: 'ogg' | 'wav';
  retentionDays: number;
  minFreeGb: number;
}

interface Security {
  outboundLimitsEnabled: boolean;
  maxOutboundPerExtensionPerHour: number;
  maxOutboundPerHour: number;
  blockedPrefixes: string[];
  auditRetentionDays: number;
}

interface AllSettings {
  general: General;
  email: Email;
  recording: Recording;
  security: Security;
}

const TONE_REGIONS = [
  { value: 'us', label: 'United States and Canada' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'de', label: 'Germany' },
  { value: 'fr', label: 'France' },
  { value: 'au', label: 'Australia' },
  { value: 'th', label: 'Thailand' },
  { value: 'sg', label: 'Singapore' },
];

const ZONES = Array.from(
  new Set([
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'UTC',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Bangkok',
    'Asia/Singapore',
    'Asia/Yangon',
    'Australia/Sydney',
  ]),
);

export function Settings() {
  const [tab, setTab] = useState('general');

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<AllSettings>('/api/v1/settings'),
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="How this PBX behaves, and how it sends email." />

      <Tabs
        tabs={[
          { id: 'general', label: 'Preferences' },
          { id: 'email', label: 'Email' },
          { id: 'recording', label: 'Recording' },
          { id: 'security', label: 'Security' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'general' && <GeneralForm initial={data.general} />}
      {tab === 'email' && <EmailForm initial={data.email} />}
      {tab === 'recording' && <RecordingForm initial={data.recording} />}
      {tab === 'security' && <SecurityForm initial={data.security} />}
    </div>
  );
}

function useSave(group: string) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.put(`/api/v1/settings/${group}`, body),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  return { mutation, saved };
}

function GeneralForm({ initial }: { initial: General }) {
  const [form, setForm] = useState(initial);
  const { mutation, saved } = useSave('general');

  useEffect(() => setForm(initial), [initial]);

  const patch = (next: Partial<General>) => setForm((f) => ({ ...f, ...next }));

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      {saved && <Alert tone="warning">Saved. Configuration was regenerated and reloaded.</Alert>}
      {mutation.error && <Alert>{(mutation.error as Error).message}</Alert>}

      <Section title="Basic">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="System name" required hint="Identifies this PBX in SIP headers.">
            <Input
              value={form.systemName}
              onChange={(e) => patch({ systemName: e.target.value })}
            />
          </Row>

          <Row
            label="Maximum call length (seconds)"
            required
            hint="A stuck channel is hung up at this point rather than running forever."
          >
            <Input
              value={String(form.maxCallDurationSec)}
              onChange={(e) => patch({ maxCallDurationSec: Number(e.target.value) || 0 })}
              inputMode="numeric"
            />
          </Row>

          <Row label="Timezone" required hint="Used for call records and schedules.">
            <Select value={form.timezone} onChange={(v) => patch({ timezone: v })}>
              {ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </Select>
          </Row>

          <Row
            label="Tone region"
            required
            hint="Ring and busy tones differ by country; the wrong ones sound broken to local callers."
          >
            <Select value={form.toneRegion} onChange={(v) => patch({ toneRegion: v })}>
              {TONE_REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Row>

          <Row label="Default prompt language" hint="Two-letter code, e.g. en.">
            <Input
              value={form.defaultLanguage}
              onChange={(e) => patch({ defaultLanguage: e.target.value })}
            />
          </Row>

          <Row
            label="Alert email"
            span
            hint="Where emergency calls and refused outbound calls are reported. Blank uses the From address on the Email tab. Needs email to be configured and working."
          >
            <Input
              type="email"
              value={form.alertEmail}
              onChange={(e) => patch({ alertEmail: e.target.value })}
              placeholder="ops@example.com"
            />
          </Row>
        </div>
      </Section>

      <FormFooter saving={mutation.isPending} />
    </form>
  );
}

function EmailForm({ initial }: { initial: Email }) {
  const [form, setForm] = useState(initial);
  const { mutation, saved } = useSave('email');
  const [test, setTest] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => setForm(initial), [initial]);

  const patch = (next: Partial<Email>) => setForm((f) => ({ ...f, ...next }));

  const runTest = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>('/api/v1/settings/email/test'),
    onSuccess: (result) => setTest({ ok: true, message: result.message }),
    onError: (err) => setTest({ ok: false, message: (err as Error).message }),
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      {saved && <Alert tone="warning">Saved.</Alert>}
      {mutation.error && <Alert>{(mutation.error as Error).message}</Alert>}
      {test && <Alert tone={test.ok ? 'warning' : 'destructive'}>{test.message}</Alert>}

      <Section
        title="Mail server"
        description="Used for voicemail-to-email. Without it, voicemail is only reachable from a phone or the admin."
        toggle={
          <Toggle
            checked={form.enabled}
            onChange={(v) => patch({ enabled: v })}
            label="Enable email"
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="Server" required>
            <Input
              value={form.host}
              onChange={(e) => patch({ host: e.target.value })}
              placeholder="smtp.example.com"
              disabled={!form.enabled}
            />
          </Row>

          <Row label="Port" required hint="587 for STARTTLS, 465 for implicit TLS.">
            <Input
              value={String(form.port)}
              onChange={(e) => patch({ port: Number(e.target.value) || 0 })}
              inputMode="numeric"
              disabled={!form.enabled}
            />
          </Row>

          <Row label="Username">
            <Input
              value={form.username}
              onChange={(e) => patch({ username: e.target.value })}
              disabled={!form.enabled}
            />
          </Row>

          <Row label="Password" hint="Stored on this server and never shown again.">
            <Input
              type="password"
              value={form.password}
              onChange={(e) => patch({ password: e.target.value })}
              disabled={!form.enabled}
            />
          </Row>

          <Row label="From address" required>
            <Input
              value={form.fromAddress}
              onChange={(e) => patch({ fromAddress: e.target.value })}
              placeholder="pbx@example.com"
              disabled={!form.enabled}
            />
          </Row>

          <Row label="From name">
            <Input
              value={form.fromName}
              onChange={(e) => patch({ fromName: e.target.value })}
              disabled={!form.enabled}
            />
          </Row>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Toggle
            checked={form.secure}
            onChange={(v) => patch({ secure: v })}
            label="Implicit TLS"
            disabled={!form.enabled}
          />
          <span className="text-sm">Connect with TLS immediately (port 465)</span>
        </div>
      </Section>

      <FormFooter saving={mutation.isPending}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => runTest.mutate()}
          disabled={runTest.isPending || !form.host}
        >
          <Mail size={14} />
          {runTest.isPending ? 'Testing…' : 'Test connection'}
        </Button>
      </FormFooter>
    </form>
  );
}

function RecordingForm({ initial }: { initial: Recording }) {
  const [form, setForm] = useState(initial);
  const { mutation, saved } = useSave('recording');

  useEffect(() => setForm(initial), [initial]);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      {saved && <Alert tone="warning">Saved. The worker picks this up on its next pass.</Alert>}
      {mutation.error && <Alert>{(mutation.error as Error).message}</Alert>}

      <Section
        title="Storage and retention"
        description="Recordings are the fastest way to fill a disk on a PBX."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Row
            label="Store recordings as"
            hint="Opus is about a tenth the size of WAV. Choose WAV only if your transcription or compliance tooling insists on it — recordings are the fastest way to fill a disk."
          >
            <Select
              value={form.format}
              onChange={(v) => setForm((f) => ({ ...f, format: v as Recording['format'] }))}
            >
              <option value="ogg">Opus (recommended)</option>
              <option value="wav">WAV</option>
            </Select>
          </Row>

          <Row
            label="Keep recordings for (days)"
            required
            hint="Older recordings are deleted automatically, audio and all."
          >
            <Input
              value={String(form.retentionDays)}
              onChange={(e) =>
                setForm((f) => ({ ...f, retentionDays: Number(e.target.value) || 0 }))
              }
              inputMode="numeric"
            />
          </Row>

          <Row
            label="Stop recording below (GB free)"
            required
            hint="A full disk stops calls entirely, so recording is what gets sacrificed first."
          >
            <Input
              value={String(form.minFreeGb)}
              onChange={(e) => setForm((f) => ({ ...f, minFreeGb: Number(e.target.value) || 0 }))}
              inputMode="numeric"
            />
          </Row>
        </div>
      </Section>

      <FormFooter saving={mutation.isPending} />
    </form>
  );
}

/**
 * Outbound abuse limits.
 *
 * Off by default and generous when on. A limit that stops a genuinely busy day is a limit an
 * administrator switches off permanently, which leaves them with nothing.
 */
function SecurityForm({ initial }: { initial: Security }) {
  const [form, setForm] = useState(initial);
  const { mutation, saved } = useSave('security');

  useEffect(() => setForm(initial), [initial]);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      {saved && (
        <Alert tone="warning">Saved. The call engine picks this up within 30 seconds.</Alert>
      )}
      {mutation.error && <Alert>{(mutation.error as Error).message}</Alert>}

      <Section
        title="Outbound call limits"
        description="Caps how many calls can leave this PBX, so a stolen extension password cannot run up a bill overnight."
        toggle={
          <Toggle
            checked={form.outboundLimitsEnabled}
            onChange={(v) => setForm((f) => ({ ...f, outboundLimitsEnabled: v }))}
            label="Enable outbound limits"
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Row
            label="Per extension, per hour"
            hint="A busy salesperson makes perhaps ten an hour. Well above that is the signal worth acting on."
          >
            <Input
              type="number"
              min={1}
              value={String(form.maxOutboundPerExtensionPerHour)}
              disabled={!form.outboundLimitsEnabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  maxOutboundPerExtensionPerHour: Number(e.target.value) || 1,
                }))
              }
            />
          </Row>
          <Row
            label="Whole system, per hour"
            hint="Catches the same attack spread across several stolen extensions."
          >
            <Input
              type="number"
              min={1}
              value={String(form.maxOutboundPerHour)}
              disabled={!form.outboundLimitsEnabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, maxOutboundPerHour: Number(e.target.value) || 1 }))
              }
            />
          </Row>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Emergency calls always go through, whatever the limits say.
        </p>
      </Section>

      <Section
        title="Blocked destinations"
        description="Number prefixes outbound calls may never reach. Applies whether or not the limits above are on."
      >
        <Row
          label="Blocked prefixes"
          span
          // Named specifically because "block expensive countries" is not actionable, and these
          // are the ranges that show up in real toll-fraud incidents.
          hint="One per line, digits only, no plus. Premium-rate and satellite ranges are the usual ones: 1900 (US premium), 882 and 883 (satellite), 88213 (Thuraya)."
        >
          <textarea
            className="min-h-28 w-full rounded-[--radius] border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={form.blockedPrefixes.join('\n')}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                blockedPrefixes: e.target.value
                  .split(/[\s,;]+/)
                  .map((p) => p.trim())
                  .filter(Boolean),
              }))
            }
            placeholder={'1900\n882\n883'}
          />
        </Row>
      </Section>

      <Section title="Operation log" description="How long the record of admin actions is kept.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="Keep entries for (days)" hint="Older entries are removed by the worker.">
            <Input
              type="number"
              min={7}
              value={String(form.auditRetentionDays)}
              onChange={(e) =>
                setForm((f) => ({ ...f, auditRetentionDays: Number(e.target.value) || 7 }))
              }
            />
          </Row>
        </div>
      </Section>

      <FormFooter saving={mutation.isPending} />
    </form>
  );
}
